<template>
  <div class="flex flex-col flex-1 gap-2 px-4 border-t">
    <div v-if="['1', '2'].includes(mode as string)">
      <h5 class="my-5">{{ $t(`painting.layout.pose`) }}</h5>
      <div
        class="flex gap-3"
        v-if="!reqData.pose.media && !reqData.pose.customPoseUrl"
      >
        <button class="btn btn-primary rounded" @click="clickPoseModal(0)">
          {{ t(`painting.people.posePreset`) }}
        </button>
        <!-- <button class="btn btn-primary rounded" @click="clickPoseModal(3)">
          {{ t(`painting.people.poseDiy`) }}
        </button> -->
        <button
          class="btn btn-primary rounded"
          @click="(uploadType = 'pose'), (showImgUploadPopup = true)"
        >
          {{ t(`painting.people.uploadPose`) }}
        </button>
      </div>
      <div v-else class="flex flex-col justify-center">
        <div class="flex flex-col gap-2 px-4 mb-4">
          <div class="flex justify-center items-center">
            <div
              class="w-36"
              v-if="reqData.pose.media || reqData.pose.customPoseUrl"
            >
              <BaseImg
                :media="{
                  url:
                    reqData.pose.media?.url ||
                    (reqData.pose.customPoseUrl &&
                      reqData.pose.customPoseUrl[0])!,
                }"
                fit="cover"
                @click="showImgUploadPopup = true"
              />
            </div>
          </div>
        </div>
        <div class="text-center">
          <button
            class="btn btn-sm btn-primary rounded"
            @click="
              (reqData.pose.media = undefined),
                (reqData.pose.mediaId = undefined),
                (reqData.pose.customPoseUrl = undefined)
            "
          >
            {{ $t("Buttons.delete") }}
          </button>
        </div>
      </div>
    </div>

    <template v-else>
      <div v-if="!isOrigin" class="font-bold mt-4">
        {{ t(`painting.layout.img`) }}
      </div>
      <div
        class="flex justify-center items-center"
        @click="(uploadType = 'env'), (showImgUploadPopup = true)"
      >
        <div
          :class="[
            'rounded-lg overflow-hidden flex justify-center items-center border  text-base-content/50 ',
            reqData.env.media ? '' : 'w-full min-h-[10rem]',
          ]"
        >
          <VanImage
            v-if="reqData.env.media"
            class="max-w-full"
            :src="reqData.env.media.url!"
            fit="cover"
          />
          <div v-else>
            <button class="btn btn-sm btn-primary rounded px-4">
              <!-- 编辑 -->
              {{ t(`painting.${isOrigin ? "origin" : "layout"}.upload`) }}
            </button>
          </div>
        </div>
      </div>
    </template>
    <template v-if="reqData.env.media">
      <div class="text-center">
        <button
          class="btn btn-primary btn-sm rounded px-4"
          @click="
            (reqData.env.media = undefined), (reqData.env.mediaId = undefined)
          "
        >
          {{ $t("Buttons.delete") }}
        </button>
      </div>
      <div class="font-bold mt-4">
        {{ t(`painting.layout.weight`) }}
      </div>
      <div class="flex justify-between w-full items-center gap-4">
        <div>0.1</div>
        <VanSlider
          v-model="reqData.env.weight"
          :min="0.1"
          :max="1"
          size="sm"
          :step="0.1"
        >
          <template #button>
            <button class="btn btn-circle btn-xs">
              {{ reqData.env.weight }}
            </button>
          </template>
        </VanSlider>
        <div>1</div>
      </div>
    </template>
  </div>

  <media-select-popup
    @on-media-selected="onMediaSelected"
    enable-use-avatar
    media-type="img"
    :multiple="false"
    v-model:show="showImgUploadPopup"
    :upload-type="7"
    :max-count="1"
    :enable-crop="true"
    :enable-go-image-gen="false"
    :cropAspectRatio="cropAspectRatio"
    showRatioSelect
    @update:crop-aspect-ratio="onCropAspectRatioChange"
    replace
  />

  <ImageTabsTraitPose
    type="Pose"
    v-model:showPoseEdit="showPoseEdit"
    v-model:showPoseType="showPoseType"
    @update:selectedMedia="onSelectPose"
    @update:selectedCustomPose="onSelectCustomPose"
  />
</template>

<script setup lang="ts">
import { StorageGet, StorageSet } from "@/tools/localStorage";
import { MediaUploadFileItem } from "@/types/component";
import { showConfirmDialog } from "vant";
import { PropType } from "vue";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
const route = useRoute();
const { mode } = route.query;

const props = defineProps({
  reqData: {
    type: Object as PropType<PromptJson>,
    required: true,
  },
  isOrigin: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["uploadNsfwImage"]);
const { t } = useI18n();
const showImgUploadPopup = ref(false);
const uploadType = ref("");

const onMediaSelected = (list: MediaUploadFileItem[]) => {
  if (list.length > 0) {
    if (uploadType.value === "env") {
      if (list[0].isNsfw) {
        emit("uploadNsfwImage", {
          id: list[0].mediaId,
          isNsfw: list[0].isNsfw,
          url: list[0].url,
        });
      } else {
        // @ts-ignore
        props.reqData.env.media = list[0];
        props.reqData.env.mediaId = list[0].mediaId;
        props.reqData.ratio = cropAspectRatio.value === 3 / 4 ? "3:4" : "16:9";
      }
    } else if (uploadType.value === "pose") {
      // @ts-ignore
      props.reqData.pose.media = { url: list[0].url };
      props.reqData.pose.mediaId = list[0].mediaId;
    }
  }
};

const cropAspectRatio = ref(3 / 4);
const onCropAspectRatioChange = (_ratio: string) => {
  const [w, h] = _ratio.split(":").map(Number);
  cropAspectRatio.value = w / h;
};

// 姿势
const showPoseEdit = ref(false);
const showPoseType = ref(0);

const clickPoseModal = async (tab: number) => {
  if (tab === 3) {
    try {
      if (!StorageGet(`Message.customPoseTip`)) {
        await showConfirmDialog({
          message: t(`Message.customPoseTip`),
          confirmButtonText: t(`Buttons.confirm`),
          cancelButtonText: t(`Buttons.cancel`),
        });
        StorageSet(`Message.customPoseTip`, 1);
      }
    } catch (e) {
      return;
    }
  }

  showPoseType.value = tab;
  showPoseEdit.value = true;
};

const onSelectPose = (list: MediaUploadFileItem) => {
  // poseImg.value = list;
  // @ts-ignore
  props.reqData.pose.media = { ...list };
  props.reqData.pose.mediaId = list.mediaId;
  props.reqData.pose.customPoseUrl = undefined;
  props.reqData.ratio = list.ratio;
  showPoseEdit.value = false;
};

const onSelectCustomPose = (list: {
  ratio: string;
  customPoseUrl: string[];
}) => {
  props.reqData.pose.customPoseUrl = list.customPoseUrl;
  props.reqData.pose.media = undefined;
  props.reqData.pose.mediaId = undefined;
  props.reqData.ratio = list.ratio;
  showPoseEdit.value = false;
};
</script>
